import React, { useState, useRef } from 'react';
import { Upload, X, Crop } from 'lucide-react';
import Cropper from 'react-easy-crop';

interface Props {
  currentImage: string;
  onChange: (url: string) => void;
  aspectRatio?: number;
  label: string;
}

export function ImageUpload({ currentImage, onChange, aspectRatio = 1, label }: Props) {
  const [previewUrl, setPreviewUrl] = useState(currentImage);
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [tempImage, setTempImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setTempImage(objectUrl);
      setShowCropper(true);
    }
  };

  const clearImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPreviewUrl('');
    setTempImage(null);
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleContainerClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onCropComplete = async (croppedArea: any, croppedAreaPixels: any) => {
    if (!tempImage) return;

    const image = new Image();
    image.src = tempImage;
    await new Promise((resolve) => {
      image.onload = resolve;
    });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;

    ctx.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      croppedAreaPixels.width,
      croppedAreaPixels.height
    );

    canvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        onChange(url);
      }
    }, 'image/jpeg', 0.9);
  };

  const handleCropSave = () => {
    setShowCropper(false);
    setTempImage(null);
  };

  if (showCropper && tempImage) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="text-lg font-semibold">Recadrer l'image</h3>
            <button
              onClick={() => {
                setShowCropper(false);
                setTempImage(null);
              }}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="relative h-96">
            <Cropper
              image={tempImage}
              crop={crop}
              zoom={zoom}
              aspect={aspectRatio}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>

          <div className="p-4 border-t">
            <div className="flex items-center gap-4 mb-4">
              <label className="text-sm font-medium text-gray-700">Zoom:</label>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1"
              />
            </div>
            <button
              onClick={handleCropSave}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Appliquer le recadrage
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="relative">
        <div 
          onClick={handleContainerClick}
          className={`relative border-2 border-dashed border-gray-300 rounded-lg overflow-hidden hover:border-purple-500 transition-colors
            ${aspectRatio === 1 ? 'aspect-square' : 'aspect-[16/9]'}`}
        >
          {previewUrl ? (
            <>
              <img
                src={previewUrl}
                alt={label}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-2 right-2 flex gap-2" onClick={e => e.stopPropagation()}>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    if (fileInputRef.current) {
                      fileInputRef.current.click();
                    }
                  }}
                  className="p-1.5 bg-white/80 hover:bg-white text-gray-700 rounded-full transition-colors"
                >
                  <Crop className="w-4 h-4" />
                </button>
                <button
                  onClick={clearImage}
                  className="p-1.5 bg-red-500/80 hover:bg-red-500 text-white rounded-full transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <Upload className="w-8 h-8 text-gray-400" />
              <span className="mt-2 text-sm text-gray-500">Cliquez ou glissez une image</span>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      </div>
    </div>
  );
}